import {
  RecordCard,
  type RecordDateMeta,
} from "@/components/portfolio/records/RecordCard";
import { Button } from "@/components/ui/Button";
import { PanelGrid } from "@/components/ui/Panel";
import type {
  PortfolioRecord,
  PortfolioRecordCategory,
} from "@/lib/portfolio-records";
import { FolderOpenIcon, PlusIcon } from "@heroicons/react/24/outline";
import type { DragEvent } from "react";

type RecordsSectionProps = {
  records: PortfolioRecord[];
  isLoading: boolean;
  isDragging: boolean;
  copy: {
    eyebrow: string;
    description: string;
    add: string;
    emptyTitle: string;
    emptyDescription: string;
    documents: string;
    dates: string;
    edit: string;
    delete: string;
    deleteTitle: string;
    deleteDescription: string;
    download: string;
    categories: Record<PortfolioRecordCategory, string>;
  };
  documentCountLabel: string;
  dateCountLabel: string;
  getDateMeta: (record: PortfolioRecord) => RecordDateMeta;
  getRecurrenceLabel: (record: PortfolioRecord) => string | null;
  getReminderLabel: (record: PortfolioRecord) => string | null;
  formatFileSize: (size: number) => string;
  onCreate: () => void;
  onEdit: (record: PortfolioRecord) => void;
  onDelete: (record: PortfolioRecord) => void;
  onDownload: (document: PortfolioRecord["documents"][number]) => void;
  onDraggingChange: (isDragging: boolean) => void;
  onEmptyDrop: (event: DragEvent<HTMLButtonElement>) => void;
};

export function RecordsSection({
  records,
  isLoading,
  isDragging,
  copy,
  documentCountLabel,
  dateCountLabel,
  getDateMeta,
  getRecurrenceLabel,
  getReminderLabel,
  formatFileSize,
  onCreate,
  onEdit,
  onDelete,
  onDownload,
  onDraggingChange,
  onEmptyDrop,
}: RecordsSectionProps) {
  return (
    <section
      className="app-section-card w-full"
      data-tour="portfolio-documents"
    >
      <div className="flex flex-col gap-4 border-b border-softiq-primary/10 px-5 py-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="app-section-eyebrow">{copy.eyebrow}</p>
            <p className="app-section-meta whitespace-nowrap tabular-nums">
              {documentCountLabel}
              <span className="mx-1.5" aria-hidden="true">
                ·
              </span>
              {dateCountLabel}
            </p>
          </div>
          <p className="app-section-description mt-1">{copy.description}</p>
        </div>
        <Button
          type="button"
          size="compact"
          startIcon={<PlusIcon aria-hidden="true" />}
          onClick={onCreate}
        >
          {copy.add}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3 p-5 sm:p-6" aria-label="Loading">
          {[0, 1].map((item) => (
            <div
              key={item}
              className="h-24 animate-pulse rounded-xl bg-softiq-primary/5 dark:bg-white/5"
            />
          ))}
        </div>
      ) : records.length === 0 ? (
        <div className="p-5 sm:p-6">
          <button
            type="button"
            className={`app-section-empty group flex-col gap-3 text-center ${
              isDragging
                ? "border-softiq-secondary bg-softiq-secondary-shade-100 text-softiq-primary"
                : ""
            }`}
            onClick={onCreate}
            onDragEnter={(event) => {
              event.preventDefault();
              onDraggingChange(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                onDraggingChange(false);
              }
            }}
            onDrop={onEmptyDrop}
          >
            <span className="flex size-10 items-center justify-center rounded-xl bg-softiq-secondary-shade-100 text-softiq-primary transition-transform group-hover:-translate-y-0.5 dark:bg-white/10 dark:text-softiq-secondary">
              <FolderOpenIcon aria-hidden="true" className="size-5" />
            </span>
            <span>
              <span className="block text-sm font-medium text-softiq-primary dark:text-white">
                {copy.emptyTitle}
              </span>
              <span className="mt-1 block">{copy.emptyDescription}</span>
            </span>
          </button>
        </div>
      ) : (
        <PanelGrid className="p-4 sm:p-5 lg:grid-cols-2">
          {records.map((record) => (
            <RecordCard
              key={record.id}
              record={record}
              categoryLabel={
                record.category !== "OTHER" && record.category !== "INSURANCE"
                  ? copy.categories[record.category]
                  : undefined
              }
              dateMeta={getDateMeta(record)}
              recurrence={getRecurrenceLabel(record)}
              reminder={getReminderLabel(record)}
              editLabel={copy.edit}
              deleteLabel={copy.delete}
              deleteTitle={copy.deleteTitle}
              deleteDescription={copy.deleteDescription}
              downloadLabel={copy.download}
              formatFileSize={formatFileSize}
              onEdit={() => onEdit(record)}
              onDelete={() => onDelete(record)}
              onDownload={onDownload}
            />
          ))}
        </PanelGrid>
      )}
    </section>
  );
}
